import { useEffect, useState } from "react";
import { Col, Row, Typography, Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import BackButton from "../../components/CustomButton/BackButton";
import {
  entNameRules,
  busNameRules,
} from "../../helpers/helpers";
import CustomButton from "../../components/CustomButton/Button";
import { BTN_TXT, DEAL_CATEGORY_IDS } from "../../constants/constants";
import { useNavigate, useLocation } from "react-router-dom";
import { getEligibleBusinessesThunk } from "../../redux/slices/dealUploadSlice";
import { PERM_CODE } from "../../constants/PermissionCodes";
import "./SupplyChainUpload.scss";

const { Title } = Typography;

const SupplyChainUpload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const businessId = location.state?.businessId;
  const dealType = location.state?.dealType || DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE;

  const { user } = useSelector((state) => state.auth);
  const { businessList: entList } = useSelector(
    (state) => state?.dealUpload
  );

  const [selectedOrg, setSelectedOrg] =
    useState(null);

  const userPrivileges =
    user?.privilege?.map(
      (p) => p.privilegeCode
    ) || [];

  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(getEligibleBusinessesThunk(dealType || DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE));
  }, [dispatch, dealType]);

  useEffect(() => {
    if (businessId && entList.length > 0) {
      const selectedBusiness = entList.find(
        (item) =>
          item.orgId === businessId &&
          item.enterprises &&
          item.enterprises.length > 0
      );

      if (selectedBusiness) {
        form.setFieldsValue({
          orgId: businessId,
        });

        setSelectedOrg(businessId);
      }
    }
  }, [businessId, entList, form]);

  const handleUploadSCF = (values) => {
    const org = entList.find(
      (o) => o.orgId === values.orgId
    );

    const enterprise =
      org?.enterprises?.find(
        (e) =>
          e.busEntId === values.entName
      );

    if (!org || !enterprise) return;

    navigate(
      "/supply-chain-upload/upload-inv-files",
      {
        state: {
          selectedOrg: org,
          selectedEnterprise: enterprise,
          selectedBusEntId:
            enterprise.busEntId,
          dealType: dealType || DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE,
          dealCategoryId: DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE,
        },
      }
    );
  };

  // Only businesses where Business, Enterprise, and Credit Limit statuses are all Active
  const filteredBusinesses = (entList || [])
    .filter((item) => {
      const busStatusVal = item.statusId || item.businessStatusId || item.orgStatusId;
      if (busStatusVal && busStatusVal !== "USM1000001") return false;
      const statusNameLower = (item.statusName || item.status || "").toLowerCase();
      if (statusNameLower && statusNameLower !== "active" && !statusNameLower.includes("active")) return false;
      return true;
    })
    .map((item) => {
      const validEnterprises = (item.enterprises || []).filter((ent) => {
        const entStatusVal = ent.statusId || ent.entStatusId || ent.enterpriseStatusId;
        if (entStatusVal && entStatusVal !== "USM1000002") return false;
        const entStatusNameLower = (ent.statusName || ent.status || "").toLowerCase();
        if (entStatusNameLower && entStatusNameLower !== "active" && !entStatusNameLower.includes("active")) return false;

        const clStatusVal = ent.busEntStatusId || ent.creditLimitStatusId;
        if (clStatusVal && clStatusVal !== "USM1000002") return false;
        const clStatusNameLower = (ent.busEntStatusName || ent.creditLimitStatusName || "").toLowerCase();
        if (clStatusNameLower && clStatusNameLower !== "active" && !clStatusNameLower.includes("active")) return false;

        return true;
      });
      return { ...item, enterprises: validEnterprises };
    })
    .filter((item) => item.enterprises && item.enterprises.length > 0);

  return (
    <>
      <BackButton />

      <Title
        level={3}
        style={{
          margin: 0,
          fontSize: 24,
        }}
      >
        Supply Chain Financing Upload
      </Title>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleUploadSCF}
        initialValues={{
          remember: true,
        }}
        className="invoice-upload-form"
      >
        <Row gutter={[16, 16]}>
          {/* Business Name Dropdown */}
          <Col xs={24} md={12}>
            <CustomSelect
              label="Business Name"
              name="orgId"
              showSearch
              placeholder="Select Business"
              className="custom-ent-select"
              options={filteredBusinesses.map(
                (item) => ({
                  value: item.orgId,
                  label: item.orgName,
                })
              )}
              rules={busNameRules}
              onChange={(value) => {
                setSelectedOrg(value);

                // Reset enterprise dropdown
                form.setFieldsValue({
                  entName: undefined,
                });
              }}
              disabled={
                !userPrivileges.includes(
                  PERM_CODE.IUEDT
                )
              }
            />
          </Col>

          {/* Enterprise Name Dropdown */}
          <Col xs={24} md={12}>
            <CustomSelect
              label="Enterprise Name"
              name="entName"
              showSearch
              placeholder="Select Enterprise"
              className="custom-ent-select"
              options={
                filteredBusinesses.find(
                  (o) =>
                    o.orgId === selectedOrg
                )?.enterprises?.map(
                  (ent) => ({
                    value: ent.busEntId,
                    label: ent.entName,
                  })
                ) || []
              }
              rules={entNameRules}
              disabled={
                !userPrivileges.includes(
                  PERM_CODE.IUEDT
                )
              }
            />
          </Col>



          {/* Submit Button */}
          <Col xs={24} md={4}>
            <Form.Item>
              <CustomButton
                type="primary"
                htmlType="submit"
                block
                style={{
                  padding: 24,
                  marginTop: 32,
                }}
              >
                {BTN_TXT.UPLOAD_SCF}
              </CustomButton>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default SupplyChainUpload;
